import { ButtonItem, Form, FormRef, GroupItem, RequiredRule, SimpleItem } from 'devextreme-react/form'
import { useRef } from 'react'
import { ArraySourceComposer } from '@/auth/api/connector'
import { task, user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { LoadPanel, NumberBox, Popup } from 'devextreme-react'
import { ValueChangedEvent as ValueChangedEventNumberBox } from 'devextreme/ui/number_box'
import 'devextreme/ui/slider'
import type { ValueChangedEvent as ValueChangedEventSlider } from 'devextreme/ui/slider'
import { NumberBoxRef } from 'devextreme-react/cjs/number-box'
import { useScreenSize } from '@/themes/media-query'
import { PopupRef } from 'devextreme-react/cjs/popup'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import notify from 'devextreme/ui/notify'
import { LoadPanelRef } from 'devextreme-react/cjs/load-panel'
import { ClickEvent } from 'devextreme/ui/button'
import { DataGridRef } from 'devextreme-react/cjs/data-grid'
import { onInitializedHtmlEditor, onValueChangedHtmlEditor } from '@/routes/utils'
import '@/routes/tasks/TaskEditor.form.scss'
import { DateBoxTypes } from 'devextreme-react/cjs/date-box'
import { confirm } from 'devextreme/ui/dialog'

export type OutlookAppointmentOnSubmitEditorParams = {
  users: (user | null | undefined)[] | undefined
  date: Date | undefined
  duration: number | undefined
  task: task | null | undefined
  remote: boolean | undefined
  provisional: boolean | undefined
  notes?: string | undefined
}
export type OutlookAppointmentSelectorProps = {
  task: task | undefined
  popupRef: React.RefObject<PopupRef>
  appointmentData?: any
  appointmentGridRef?: React.RefObject<DataGridRef>
  isEditing?: boolean
}

export const OutlookAppointmentSelector = (props: OutlookAppointmentSelectorProps) => {
  const { popupRef, task, appointmentData, appointmentGridRef, isEditing } = props
  const formRef = useRef<FormRef>(null)
  const durataNumberBoxRef = useRef<NumberBoxRef>(null)
  const currentScreenSize = useScreenSize()
  const { client } = useQsAdminApiManager()
  const loadPanelRef = useRef<LoadPanelRef>(null)

  const dateTaskAppointment = task?.consegna_prevista ? new Date(task.consegna_prevista) : new Date()
  dateTaskAppointment.setHours(8)
  dateTaskAppointment.setMinutes(30)
  dateTaskAppointment.setSeconds(0)
  dateTaskAppointment.setMilliseconds(0)

  const tecnici: (user | null | undefined)[] = []
  tecnici.push(task?.resp_tecnico ?? null)
  task?.tecnici?.map((tecnico: user) => {
    tecnici.push(tecnico ?? null)
  })

  const formData: {
    data: Date
    durata: number
    utenti: (user | null | undefined)[]
    remoto: boolean
    provvisorio: boolean
    note: string
    subject: string
  } = {
    data: appointmentData ? new Date(appointmentData.StartDate) : dateTaskAppointment,
    durata: appointmentData ? appointmentData.Duration : 9,
    utenti: appointmentData
      ? [
          {
            id: appointmentData.User.id,
            fullname: appointmentData.User.fullName,
            email: appointmentData.User.email,
            cognome: null,
            nome: null,
            note: null,
            attivo: false,
            contractor: false,
            email_ad: null,
          },
        ]
      : tecnici,
    remoto: appointmentData ? appointmentData.Remote : false,
    provvisorio: appointmentData ? appointmentData.Provisional : false,
    note: appointmentData ? appointmentData.Notes : '',
    subject: appointmentData ? appointmentData.Subject : 'Task: ' + task?.ded_Dis,
  }

  const resetForm = () => {
    formRef.current?.instance().updateData('data', dateTaskAppointment)
    formRef.current?.instance().updateData('durata', 9)
    formRef.current?.instance().updateData('utenti', tecnici)
    formRef.current?.instance().updateData('remoto', false)
    formRef.current?.instance().updateData('provvisorio', false)
    formRef.current?.instance().updateData('note', '')
    formRef.current?.instance().updateData('subject', null)
  }

  const onShowing = () => {
    formRef.current
      ?.instance()
      .updateData('subject', appointmentData ? appointmentData.Subject : 'Task: ' + task?.ded_Dis)
  }

  const submitAppointment = async (ignoreBusyUsers: boolean) => {
    loadPanelRef.current?.instance().option('visible', true)
    try {
      if (isEditing && appointmentData) {
        await client.put(
          'api/calendar/task/update',
          {
            id: appointmentData.id,
            userEmail: appointmentData.User.email,
            date: formData.data,
            duration: formData.durata,
            task,
            remote: formData.remoto,
            provisional: formData.provvisorio,
            notes: formData.note,
            ignoreBusyUsers,
            subject: formData.subject,
          },
          { headers: { 'Content-Type': 'application/json' } },
        )
        notify(`Modifica appuntamento terminata con successo.`, 'success', 2000)
      } else {
        await client.post(
          'api/calendar/task/create',
          {
            users: formData.utenti,
            date: formData.data,
            duration: formData.durata,
            task,
            remote: formData.remoto,
            provisional: formData.provvisorio,
            notes: formData.note,
            ignoreBusyUsers,
            subject: formData.subject,
          },
          { headers: { 'Content-Type': 'application/json' } },
        )
        notify(`Creazione appuntamento terminata con successo.`, 'success', 2000)
      }
      if (appointmentGridRef) appointmentGridRef.current?.instance().refresh()
      popupRef.current?.instance().hide()
      resetForm()
    } catch (error: any) {
      if (error.response?.status === 409) {
        const detected: string[] = error.response.data?.busyUsers ?? []
        const busyList = detected.join(', ')
        const result = confirm(
          `<i>I seguenti utenti risultano <b>occupati</b> nell'arco di tempo selezionato: <b>${busyList}</b>.<br/>Vuoi procedere comunque?</i>`,
          'Utenti occupati',
        )
        result.then(async (dialogResult) => {
          if (dialogResult === false) return
          await submitAppointment(true)
        })
      } else {
        notify(
          `Errore ${isEditing ? 'modifica' : 'creazione'} appuntamento per il task ${task?.ded_Dis}. Dettagli : ${error.response?.data?.error?.message ?? error}`,
          'error',
          3000,
        )
      }
    } finally {
      loadPanelRef.current?.instance().option('visible', false)
    }
  }

  return (
    <>
      <Popup
        width={currentScreenSize.isLarge || currentScreenSize.isMedium ? 700 : currentScreenSize.isXSmall ? 350 : 700}
        height={currentScreenSize.isSmall ? 320 : currentScreenSize.isXSmall ? 720 : 'auto'}
        hideOnOutsideClick={true}
        title={isEditing ? 'Modifica Appuntamento' : 'Selezionare Data Appuntamento'}
        showCloseButton={true}
        deferRendering={true}
        ref={popupRef}
        onShowing={onShowing}
        onHiding={resetForm}
        resizeEnabled={false}
      >
        <div id="outlook-appointment-selector-form-container" className={'dx-card responsive-paddings'}>
          <LoadPanel
            ref={loadPanelRef}
            shadingColor="rgba(0,0,0,0.4)"
            showIndicator={true}
            shading={true}
            showPane={true}
            hideOnOutsideClick={false}
            hideOnParentScroll={false}
            position={{ of: '#outlook-appointment-selector-form-container' }}
          />

          <Form
            id={`outlook-appointment-selector-form}`}
            key={`outlook-appointment-selector-form}`}
            formData={formData}
            ref={formRef}
            validationGroup="outlookAppointmentSelectorValidation"
          >
            <GroupItem colCount={1}>
              <SimpleItem dataField="subject" editorType="dxTextBox" label={{ text: 'Titolo' }}>
                <RequiredRule message="Titolo obbligatorio" />
              </SimpleItem>
              <SimpleItem
                dataField="utenti"
                editorType="dxTagBox"
                label={{ text: 'Utenti' }}
                editorOptions={{
                  dataSource: ArraySourceComposer('id', tecnici),
                  displayExpr: 'fullname',
                  placeholder: 'Seleziona utenti...',
                  applyValueMode: 'instantly',
                  showSelectionControls: true,
                  searchEnabled: true,
                  readOnly: isEditing === true,
                }}
              >
                <RequiredRule message="Almeno un utente obbligatorio" />
              </SimpleItem>
              <SimpleItem
                dataField="data"
                editorOptions={{
                  displayFormat: 'dd/MM/yyyy HH:mm',
                  type: 'datetime',
                  pickerType: 'calendar',
                  applyValueMode: 'instantly',
                  onOpened: (e: DateBoxTypes.OpenedEvent) => {
                    formRef.current?.instance().getButton('btn-compute')?.option('disabled', true)
                  },
                  onClosed: (e: DateBoxTypes.ClosedEvent) => {
                    formRef.current?.instance().getButton('btn-compute')?.option('disabled', false)
                  },
                }}
                editorType="dxDateBox"
                label={{ text: 'Data' }}
              >
                <RequiredRule message="Data obbligatoria" />
              </SimpleItem>
              <GroupItem colCount={2}>
                <SimpleItem
                  dataField="durata"
                  editorType="dxSlider"
                  label={{ text: 'Durata' }}
                  editorOptions={{
                    max: 10,
                    min: 1,
                    step: 0.5,
                    onValueChanged: (e: ValueChangedEventSlider) => {
                      durataNumberBoxRef.current?.instance().option('value', e.value)
                    },
                  }}
                >
                  <RequiredRule message="Durata obbligatoria" />
                </SimpleItem>
                <SimpleItem label={{ text: 'Valore' }}>
                  <NumberBox
                    ref={durataNumberBoxRef}
                    max={10}
                    min={1}
                    step={0.5}
                    defaultValue={formData?.durata}
                    showSpinButtons={true}
                    onValueChanged={(e: ValueChangedEventNumberBox) => {
                      formRef.current?.instance().updateData('durata', e.value)
                    }}
                  />
                </SimpleItem>
              </GroupItem>
            </GroupItem>
            <GroupItem colCount={1}>
              <SimpleItem
                dataField="note"
                label={{ text: 'Note' }}
                editorType="dxHtmlEditor"
                editorOptions={{
                  stylingMode: 'filled',
                  valueType: 'html',
                  mediaResizing: { enabled: true },
                  imageUpload: { tabs: ['file', 'url'], fileUploadMode: 'base64' },
                  height: 300,
                  onValueChanged: onValueChangedHtmlEditor,
                  onInitialized: onInitializedHtmlEditor,
                }}
              />
            </GroupItem>
            <GroupItem colCount={2}>
              <SimpleItem dataField="remoto" label={{ text: 'Remoto' }} editorType="dxSwitch" />
              <SimpleItem dataField="provvisorio" label={{ text: 'Provvisorio' }} editorType="dxSwitch" />
            </GroupItem>
            <GroupItem cssClass="last-group">
              <GroupItem cssClass="buttons-group" colCount={1}>
                <ButtonItem
                  name="btn-compute"
                  buttonOptions={{
                    icon: isEditing ? 'edit' : 'group',
                    text: isEditing ? 'Modifica' : 'Crea',
                    width: '150px',
                    onClick: async (e: ClickEvent) => {
                      const validationResult = formRef.current?.instance().validate()
                      if (!validationResult?.isValid) return
                      await submitAppointment(false) // normal submit, busy check active
                    },
                  }}
                />
              </GroupItem>
            </GroupItem>
          </Form>
        </div>
      </Popup>
    </>
  )
}

export default OutlookAppointmentSelector
